import * as React from 'react';

import { CompactBar, CompactTitle, PageHeader, useScrolledPast } from '@/design';
import { WorkPageHeaderActions, type IHeaderRefresh } from './lists/work-page/header-actions';

/**
 * Team routing's header (SR-513) — a light work page's (the work pages design, "Three forms"): the
 * title, and at the right *Refresh* with "updated hh:mm" and the "?". No tabs, no signals, and no
 * count read of its own: the Firms table's counts come with its rows' read.
 *
 * Scrolled past the page's own controls — the sentinel `useControlsEnd` hands the page — the app
 * bar's row draws the title, the page's `compact` controls and the same *Refresh* and "?".
 */
export function TeamRoutingHeader({
  refresh,
  help,
  scrolled,
  compact
}: {
  refresh: IHeaderRefresh;
  help: React.ReactNode;
  scrolled: boolean;
  /** The page's controls as the compact bar draws them — its search, filter and action. */
  compact?: React.ReactNode;
}) {
  return (
    <>
      <PageHeader
        title="Team routing"
        row
        actions={<WorkPageHeaderActions summary={null} refresh={refresh} help={help} />}
      />
      <CompactBar shown={scrolled} label="Team routing, compact">
        <CompactTitle>Team routing</CompactTitle>
        <span className="flex min-w-0 flex-1 items-center justify-end gap-2">{compact}</span>
        <span className="flex shrink-0 items-center gap-1">
          <WorkPageHeaderActions summary={null} refresh={refresh} help={help} />
        </span>
      </CompactBar>
    </>
  );
}

/** Where the page's own controls end — the element to draw there, and whether it has scrolled up
 *  under the app bar, which is when the compact bar stands for them. */
export function useControlsEnd(): { end: React.ReactNode; scrolled: boolean } {
  const [controlsEnd, setControlsEnd] = React.useState<Nullable<HTMLElement>>(null);
  const scrolled = useScrolledPast(controlsEnd);
  return {
    end: <div ref={setControlsEnd} aria-hidden data-controls-end="" className="-mt-3 h-0" />,
    scrolled
  };
}
